// Web Crypto API による暗号化モジュール
// 旧 Q4CL-client/utils/encryption.js から完全移植 (ロジック不変)
// 使い方:
//   await window.encryption.setEncryptParams(publicKeyPem)
//   const enc = await window.encryption.encryptStr('hello')
//   const params = window.encryption.encryptParams  // info.encryption に入れる
;(function () {
  const fixedEncKey = new TextEncoder().encode('All rights reserved by N Umetani'); // 32 bytes
  const fixedIv = new TextEncoder().encode('0123456789abcdef'); // 16 bytes

  // ---- ユーティリティ ----
  function arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  }

  function base64ToArrayBuffer(base64) {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
    return bytes.buffer;
  }

  function arrayBufferToHex(buffer) {
    const bytes = new Uint8Array(buffer);
    return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  function hexToArrayBuffer(hex) {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
    return bytes.buffer;
  }

  function encodeLength(length) {
    if (length < 128) return [length];
    if (length < 256) return [0x81, length];
    return [0x82, (length >> 8) & 0xff, length & 0xff];
  }

  // PKCS#1 RSA 公開鍵を PKCS#8 (SubjectPublicKeyInfo) にラップ
  function pkcs1ToPkcs8(pkcs1Bytes) {
    const pkcs1 = new Uint8Array(pkcs1Bytes);
    const oid = new Uint8Array([0x06, 0x09, 0x2a, 0x86, 0x48, 0x86, 0xf7, 0x0d, 0x01, 0x01, 0x01]);
    const algorithmIdentifier = new Uint8Array([0x30, 0x0d, ...oid, 0x05, 0x00]);
    const bitStringHeader = new Uint8Array([0x03, ...encodeLength(pkcs1.length + 1), 0x00]);
    const inner = new Uint8Array([...algorithmIdentifier, ...bitStringHeader, ...pkcs1]);
    const result = new Uint8Array([0x30, ...encodeLength(inner.length), ...inner]);
    return result.buffer;
  }

  async function importPublicKey(pem) {
    if (!pem) throw new Error('Public key is null or undefined');
    const isPKCS1 = pem.includes('RSA PUBLIC KEY');
    let pemContents;
    if (isPKCS1) {
      pemContents = pem
        .replace('-----BEGIN RSA PUBLIC KEY-----', '')
        .replace('-----END RSA PUBLIC KEY-----', '')
        .replace(/\s/g, '');
    } else {
      pemContents = pem
        .replace('-----BEGIN PUBLIC KEY-----', '')
        .replace('-----END PUBLIC KEY-----', '')
        .replace(/\s/g, '');
    }
    let binaryDer = base64ToArrayBuffer(pemContents);
    if (isPKCS1) binaryDer = pkcs1ToPkcs8(binaryDer);
    // 旧旧版 (Q4CL-client_old_version.xml:4741) は Node.js `crypto.publicEncrypt(publicKey, buffer)`
    // のデフォルト (RSA_PKCS1_OAEP_PADDING + SHA-1) で暗号化していた。
    // console 側 (q4cl-console) も同じ前提で privateDecrypt しているため、 ここも SHA-1 にする必要あり。
    // SHA-256 にすると console で `decryption error` になる。
    return await crypto.subtle.importKey('spki', binaryDer, { name: 'RSA-OAEP', hash: 'SHA-1' }, true, ['encrypt']);
  }

  // ---- メイン API ----
  const enc = {
    encKey: null, // Uint8Array(32)
    encryptParams: null, // { publicKey, encryptedEncKey, iv }

    async sha256(s) {
      const data = new TextEncoder().encode(s);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      return arrayBufferToHex(hashBuffer);
    },

    // 旧旧版踏襲 (Q4CL-client_old_version.xml:4838-4843):
    //   md5hex: function(s) {
    //     return crypto.createHash("md5").update(s).digest("hex");
    //   }
    // → 本物の MD5 (32 文字 hex)。 単純な擬似 hash では console 側の deviceId 互換性が崩れる。
    // CryptoJS の MD5 を使用 (index.html で CDN 読込み済み)。
    md5hex(s) {
      if (s === null || typeof s === 'undefined') return '';
      if (typeof CryptoJS !== 'undefined' && CryptoJS.MD5) {
        return CryptoJS.MD5(String(s)).toString(CryptoJS.enc.Hex);
      }
      // fallback (CryptoJS 未ロード時): 空文字を返して送信を止める
      console.error('[encryption.md5hex] CryptoJS が読込まれていません');
      return '';
    },

    async publicEncrypt(publicKeyPem, data) {
      const publicKey = await importPublicKey(publicKeyPem);
      const encrypted = await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, publicKey, data);
      return arrayBufferToBase64(encrypted);
    },

    /**
     * 公開鍵で AES キーを暗号化したセッションキーを発行 (SSL 方式)
     * 旧旧版 (Q4CL-client_old_version.xml:4736-4743) と完全互換:
     *   encKey = Buffer.from(crypto.randomBytes(16).toString("hex")) // 32 byte ASCII hex
     *   iv     = crypto.randomBytes(8).toString("hex")               // 16 文字 hex 文字列
     *   encryptedEncKey = crypto.publicEncrypt(pubkey, encKey).toString("base64")
     * console 側は encKey を UTF-8 文字列として復号して使うので、
     * 単純なランダム 32 byte ではなく ASCII hex 文字列 (32 byte) でなければならない。
     */
    async setEncryptParams(publicKeyPem) {
      // 16 byte ランダム → hex 化 (32 文字) → UTF-8 で 32 byte の Uint8Array
      const rawKey = new Uint8Array(16);
      crypto.getRandomValues(rawKey);
      const hexKey = arrayBufferToHex(rawKey); // 32 chars
      this.encKey = new TextEncoder().encode(hexKey); // 32 byte ASCII

      // 8 byte ランダム → hex 化 (16 文字)。 IV は文字列のまま encryptParams.iv に格納し、
      // AES-CBC に渡すときは UTF-8 encode して 16 byte ASCII にする。
      const rawIv = new Uint8Array(8);
      crypto.getRandomValues(rawIv);
      const hexIv = arrayBufferToHex(rawIv); // 16 chars

      const encryptedEncKey = await this.publicEncrypt(publicKeyPem, this.encKey);
      this.encryptParams = {
        publicKey: publicKeyPem,
        encryptedEncKey: encryptedEncKey,
        iv: hexIv
      };
      return this.encryptParams;
    },

    // 旧旧版互換: encryptParams.iv は 16 文字の hex 文字列。
    // AES-CBC に渡す IV (16 byte) は UTF-8 で encode した ASCII バイト列。
    _ivBytes() {
      return new TextEncoder().encode(this.encryptParams.iv);
    },

    /**
     * AES-256-CBC 文字列暗号化 (本セッション鍵を使用)
     */
    async encryptStr(s) {
      if (s === null || typeof s === 'undefined') return '';
      if (!this.encKey) {
        // debug / publicKey 未設定時のフォールバック (生の値を返して送信は止める前提)
        return '[unencrypted:' + String(s) + ']';
      }
      try {
        const key = await crypto.subtle.importKey('raw', this.encKey, { name: 'AES-CBC' }, false, ['encrypt']);
        const iv = this._ivBytes(); // 旧旧版互換: iv 文字列 (16 文字) を UTF-8 で 16 byte 化
        const data = new TextEncoder().encode(s);
        const encrypted = await crypto.subtle.encrypt({ name: 'AES-CBC', iv }, key, data);
        return arrayBufferToBase64(encrypted);
      } catch (error) {
        console.error('encryptStr error:', error);
        return '';
      }
    },

    /**
     * AES-256-CBC 復号 (受信側用 — Q4CL-console と同形式)
     * encParams.iv は 16 文字の hex 文字列 (旧旧版踏襲)。 UTF-8 で 16 byte 化して使う。
     * encParams.encKey は 32 文字の hex 文字列 (UTF-8 で 32 byte 化したもの) を想定。
     */
    async decryptStr(s, encParams) {
      if (s === null || typeof s === 'undefined' || s === '') return '';
      try {
        const keyBytes = typeof encParams.encKey === 'string'
          ? new TextEncoder().encode(encParams.encKey)
          : encParams.encKey;
        const key = await crypto.subtle.importKey('raw', keyBytes, { name: 'AES-CBC' }, false, ['decrypt']);
        const iv = typeof encParams.iv === 'string' ? new TextEncoder().encode(encParams.iv) : encParams.iv;
        const encrypted = base64ToArrayBuffer(s);
        const decrypted = await crypto.subtle.decrypt({ name: 'AES-CBC', iv }, key, encrypted);
        const decryptedText = new TextDecoder().decode(decrypted);

        if (decryptedText.slice(64, 69) === 'data:') {
          const img = decryptedText.slice(64);
          const hash = decryptedText.slice(0, 64);
          // 旧旧版同様 sha256(img + encKeyAsString)
          const keyAsString = typeof encParams.encKey === 'string'
            ? encParams.encKey
            : new TextDecoder().decode(keyBytes);
          const computedHash = await this.sha256(img + keyAsString);
          return computedHash === hash ? img : '';
        }
        return decryptedText.slice(0, 10000);
      } catch (error) {
        console.error('decryptStr error:', error);
        return '';
      }
    },

    /**
     * SHA-256 ハッシュ付き AES 暗号化 (画像データの改ざん検知用)
     */
    async encryptStrWithHash(s) {
      if (s === null || typeof s === 'undefined' || s === '') return '';
      if (!this.encKey) {
        return '[unencrypted:' + String(s).slice(0, 64) + '...]';
      }
      try {
        // 旧旧版互換: hash = sha256(s + encKeyAsString)。
        // encKey は UTF-8 で encode された ASCII hex (32 文字) なので TextDecoder で文字列化
        const encKeyAsString = new TextDecoder().decode(this.encKey);
        const hashInput = s + encKeyAsString;
        const sha256Hash = await this.sha256(hashInput);
        const dataWithHash = sha256Hash + s;

        const key = await crypto.subtle.importKey('raw', this.encKey, { name: 'AES-CBC' }, false, ['encrypt']);
        const iv = this._ivBytes();
        const data = new TextEncoder().encode(dataWithHash);
        const encrypted = await crypto.subtle.encrypt({ name: 'AES-CBC', iv }, key, data);
        return arrayBufferToBase64(encrypted);
      } catch (error) {
        console.error('encryptStrWithHash error:', error);
        return '';
      }
    },

    /**
     * 固定鍵での AES 暗号化 (メール送信 API 用)
     */
    async encryptStrAes(s) {
      if (s === null || typeof s === 'undefined') return '';
      try {
        const key = await crypto.subtle.importKey('raw', fixedEncKey, { name: 'AES-CBC' }, false, ['encrypt']);
        const data = new TextEncoder().encode(s);
        const encrypted = await crypto.subtle.encrypt({ name: 'AES-CBC', iv: fixedIv }, key, data);
        return arrayBufferToBase64(encrypted);
      } catch (error) {
        console.error('encryptStrAes error:', error);
        return '';
      }
    },

    /**
     * 固定鍵での AES 復号
     */
    async decryptStrAes(cipherText) {
      if (cipherText === null || typeof cipherText === 'undefined') return '';
      try {
        const key = await crypto.subtle.importKey('raw', fixedEncKey, { name: 'AES-CBC' }, false, ['decrypt']);
        const encrypted = base64ToArrayBuffer(cipherText);
        const decrypted = await crypto.subtle.decrypt({ name: 'AES-CBC', iv: fixedIv }, key, encrypted);
        return new TextDecoder().decode(decrypted);
      } catch (error) {
        console.error('decryptStrAes error:', error);
        return '';
      }
    },

    // 公開ユーティリティ (外から使えるように)
    _arrayBufferToBase64: arrayBufferToBase64,
    _base64ToArrayBuffer: base64ToArrayBuffer,
    _arrayBufferToHex: arrayBufferToHex,
    _hexToArrayBuffer: hexToArrayBuffer
  };

  window.encryption = enc;
})();
